import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from './supabaseClient';
import { usePanelOwnerUserId } from './PanelAccessContext';
import { useShopsFromDb } from './useShopsFromDb';
import {
  parseShiftWorkbook,
  buildShiftInsertRows,
  downloadShiftTemplate,
} from './shiftExcelImport';
import { collectDayManagers } from './shiftUtils';
import { excelDutyLabelForStaff } from './staffRoleUtils';
import './ShiftSchedulerPage.css';

const DAYS = [
  { value: 'Pzt', label: 'Pazartesi', short: 'Pzt' },
  { value: 'Sal', label: 'Salı', short: 'Sal' },
  { value: 'Car', label: 'Çarşamba', short: 'Çar' },
  { value: 'Per', label: 'Perşembe', short: 'Per' },
  { value: 'Cum', label: 'Cuma', short: 'Cum' },
  { value: 'Cmt', label: 'Cumartesi', short: 'Cmt' },
  { value: 'Paz', label: 'Pazar', short: 'Paz' },
];

function getMondayIso(date = new Date()) {
  const d = new Date(date);
  d.setHours(12, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function formatWeekLabel(isoMonday) {
  const mon = new Date(`${isoMonday}T12:00:00`);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  return `${mon.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })} – ${sun.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}`;
}

function normalizeDay(day) {
  if (!day) return day;
  const d = String(day).trim();
  if (d === 'Çar') return 'Car';
  return d;
}

function shiftMatchesShop(shift, shopId) {
  if (shopId == null) return shift.shop_id == null;
  return Number(shift.shop_id) === Number(shopId);
}

function sortShiftsBySlot(a, b) {
  const byTime = (a.start_time || '').localeCompare(b.start_time || '');
  if (byTime !== 0) return byTime;
  return (a.sort_order ?? 0) - (b.sort_order ?? 0);
}

function countShiftsPerDay(shopShifts) {
  const countByDay = {};
  for (const s of shopShifts) {
    const code = normalizeDay(s.day);
    if (code) countByDay[code] = (countByDay[code] || 0) + 1;
  }
  return countByDay;
}

function shopWeekStatus(shopShifts, staffByAuthId) {
  const countByDay = countShiftsPerDay(shopShifts);
  const incompleteDays = DAYS.filter((d) => (countByDay[d.value] || 0) === 0).length;
  const { vm, md } = collectDayManagers(shopShifts, staffByAuthId);
  const complete = incompleteDays === 0 && shopShifts.length > 0;
  return { incompleteDays, vmCount: vm.length, mdCount: md.length, complete };
}

function ShiftRoleBadge({ role }) {
  if (!role || role === 'Barista') return null;
  const kind = role.toLowerCase();
  return (
    <span className={`shift-role-badge shift-role-badge--${kind}`} title={role === 'VM' ? 'Vardiya müdürü' : 'Müdür'}>
      {role}
    </span>
  );
}

function formatPeopleWithRoles(people, assignedIds, staffByAuthId) {
  const lines = [];
  const extra = Array.isArray(people) ? [...people] : [];

  (assignedIds || []).forEach((id) => {
    const member = staffByAuthId[id];
    const name = member?.full_name || '?';
    const idx = extra.findIndex(
      (n) => String(n).toLocaleLowerCase('tr-TR') === name.toLocaleLowerCase('tr-TR'),
    );
    if (idx >= 0) extra.splice(idx, 1);
    lines.push({ key: id, name, role: excelDutyLabelForStaff(member) });
  });

  extra.forEach((name, i) => {
    lines.push({ key: `extra-${i}`, name, role: null, unmatched: true });
  });

  return lines;
}

function formatPeoplePreviewText(people, assignedIds, staffByAuthId) {
  return formatPeopleWithRoles(people, assignedIds, staffByAuthId)
    .map((c) => (c.role && c.role !== 'Barista' ? `${c.name} (${c.role})` : c.name))
    .join(', ') || '—';
}

function WeekShiftCell({ shift, staffByAuthId }) {
  if (!shift) {
    return <span className="shift-cell-empty">—</span>;
  }

  const ids = Array.isArray(shift.assigned_user_ids) ? shift.assigned_user_ids : [];
  const chips = formatPeopleWithRoles(shift.people, ids, staffByAuthId);

  return (
    <div className="shift-cell-inner">
      <div className="shift-cell-times">
        {shift.start_time} – {shift.end_time}
      </div>
      {chips.length > 0 ? (
        <ul className="shift-cell-people">
          {chips.map((c) => (
            <li
              key={c.key}
              className={[
                'shift-person-chip',
                c.role === 'VM' ? 'is-manager is-vm' : '',
                c.role === 'MD' ? 'is-md' : '',
                c.unmatched ? 'is-unmatched' : '',
              ].filter(Boolean).join(' ')}
            >
              <span className="shift-person-name">{c.name}</span>
              <ShiftRoleBadge role={c.role} />
            </li>
          ))}
        </ul>
      ) : (
        <span className="shift-cell-no-staff">Personel yok</span>
      )}
    </div>
  );
}

function DayManagersSummary({ shifts, staffByAuthId }) {
  const { vm, md } = collectDayManagers(shifts, staffByAuthId);
  if (!vm.length && !md.length) return null;

  return (
    <div className="shift-day-managers">
      {vm.length > 0 ? (
        <div className="shift-day-managers-row">
          <span className="shift-role-badge shift-role-badge--vm">VM</span>
          <span className="shift-day-managers-label">Vardiya müdürü:</span>
          <span className="shift-day-managers-names">{vm.join(', ')}</span>
        </div>
      ) : null}
      {md.length > 0 ? (
        <div className="shift-day-managers-row">
          <span className="shift-role-badge shift-role-badge--md">MD</span>
          <span className="shift-day-managers-label">Müdür:</span>
          <span className="shift-day-managers-names">{md.join(', ')}</span>
        </div>
      ) : null}
    </div>
  );
}

function ShiftSchedulerPage() {
  const { rawRows: shopRows, loading: shopsLoading } = useShopsFromDb();
  const shopNameById = useMemo(
    () => Object.fromEntries(
      shopRows.map((r) => [
        String(r.id),
        r.shop && String(r.shop).trim() ? String(r.shop).trim() : `Mağaza ${r.id}`,
      ]),
    ),
    [shopRows],
  );

  const [allShifts, setAllShifts] = useState([]);
  const [selectedShopId, setSelectedShopId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [importMessage, setImportMessage] = useState('');
  const [userId, setUserId] = useState(null);
  const ownerUserId = usePanelOwnerUserId(userId);
  const [weekStart, setWeekStart] = useState(getMondayIso);
  const [ekipStaff, setEkipStaff] = useState([]);
  const [importPreview, setImportPreview] = useState(null);
  const [importMode, setImportMode] = useState('replace');
  const [applyToAllShops, setApplyToAllShops] = useState(true);
  const importInputRef = useRef(null);

  useEffect(() => {
    if (!shopRows.length) {
      setSelectedShopId(null);
      return;
    }
    setSelectedShopId((prev) => {
      if (prev != null && shopRows.some((s) => s.id === prev)) return prev;
      return shopRows[0].id;
    });
  }, [shopRows]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data?.user?.id));
  }, []);

  const fetchEkipStaff = useCallback(async () => {
    const { data, error: qErr } = await supabase
      .from('staff_directory')
      .select('id, full_name, auth_user_id, staff_role, manager_kind, assigned_shop_ids')
      .in('staff_role', ['barista', 'manager'])
      .order('full_name');
    if (!qErr && data) setEkipStaff(data);
  }, []);

  useEffect(() => {
    fetchEkipStaff();
  }, [fetchEkipStaff]);

  const fetchShifts = useCallback(async () => {
    setLoading(true);
    const { data, error: fetchError } = await supabase
      .from('shifts')
      .select('*')
      .eq('week_start', weekStart)
      .order('sort_order', { ascending: true });
    if (fetchError) setError(fetchError.message);
    else setAllShifts(data || []);
    setLoading(false);
  }, [weekStart]);

  useEffect(() => { fetchShifts(); }, [fetchShifts]);

  const staffByAuthId = useMemo(
    () => Object.fromEntries(
      ekipStaff.filter((m) => m.auth_user_id).map((m) => [m.auth_user_id, m]),
    ),
    [ekipStaff],
  );

  const activeShifts = useMemo(
    () => allShifts.filter((s) => shiftMatchesShop(s, selectedShopId)),
    [allShifts, selectedShopId],
  );

  const selectedShopName = selectedShopId != null
    ? (shopNameById[String(selectedShopId)] || `Mağaza ${selectedShopId}`)
    : '';

  const weekRows = useMemo(() => DAYS.map((day) => ({
    ...day,
    shifts: activeShifts
      .filter((s) => normalizeDay(s.day) === day.value)
      .sort(sortShiftsBySlot),
  })), [activeShifts]);

  const shopStatuses = useMemo(() => shopRows.map((shop) => {
    const shopShifts = allShifts.filter((s) => shiftMatchesShop(s, shop.id));
    const status = shopWeekStatus(shopShifts, staffByAuthId);
    const name = shopNameById[String(shop.id)] || `Mağaza ${shop.id}`;
    return { id: shop.id, name, ...status };
  }), [shopRows, allShifts, staffByAuthId, shopNameById]);

  const weekStats = useMemo(() => {
    const status = shopWeekStatus(activeShifts, staffByAuthId);
    const shopsReady = shopStatuses.filter((s) => s.complete).length;
    return { ...status, shopsReady, shopCount: shopRows.length };
  }, [activeShifts, staffByAuthId, shopStatuses, shopRows.length]);

  const goPrevWeek = () => {
    const d = new Date(`${weekStart}T12:00:00`);
    d.setDate(d.getDate() - 7);
    setWeekStart(getMondayIso(d));
  };

  const goNextWeek = () => {
    const d = new Date(`${weekStart}T12:00:00`);
    d.setDate(d.getDate() + 7);
    setWeekStart(getMondayIso(d));
  };

  const goCurrentWeek = () => setWeekStart(getMondayIso());

  const handleImportFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setError('');
    setImportMessage('');
    try {
      const buffer = await file.arrayBuffer();
      const parsed = parseShiftWorkbook(buffer, {
        shops: shopRows,
        staff: ekipStaff,
        fallbackShopId: selectedShopId,
        applyToAllShops,
      });
      setImportPreview({ ...parsed, fileName: file.name });
    } catch (e) {
      setImportPreview(null);
      setError(e?.message || 'Excel dosyası okunamadı.');
    } finally {
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  const runImport = async () => {
    if (!importPreview || !ownerUserId) return;

    const {
      shifts,
      shopIds,
      weeks,
      warnings,
      errors,
      fileName,
      appliedToAllShops: appliedAll,
    } = importPreview;
    if (errors?.length) {
      setError(`Excel'de ${errors.length} hata var. Önce dosyayı düzeltin.`);
      return;
    }
    if (!shifts.length) {
      setError('Yüklenecek vardiya yok.');
      return;
    }

    if (importMode === 'replace') {
      const scopeLabel = appliedAll
        ? `tüm ${shopIds.length} şube`
        : `${shopIds.length} şube`;
      const ok = window.confirm(
        `${scopeLabel}, ${weeks.length} hafta için mevcut vardiyalar silinip Excel'deki ${shifts.length} vardiya yüklenecek.\n\nDosya: ${fileName}\n\nDevam?`,
      );
      if (!ok) return;
    }

    setImporting(true);
    setError('');
    setImportMessage('');

    try {
      if (importMode === 'replace') {
        for (const shopId of shopIds) {
          for (const week of weeks) {
            const { error: delErr } = await supabase
              .from('shifts')
              .delete()
              .eq('shop_id', shopId)
              .eq('week_start', week);
            if (delErr) throw delErr;
          }
        }
      }

      const maxOrder = allShifts.length
        ? Math.max(...allShifts.map((s) => s.sort_order)) + 1
        : 0;
      const rows = buildShiftInsertRows({ shifts, userId: ownerUserId, sortOrderStart: maxOrder });

      const chunkSize = 100;
      for (let i = 0; i < rows.length; i += chunkSize) {
        const chunk = rows.slice(i, i + chunkSize);
        const { error: insErr } = await supabase.from('shifts').insert(chunk);
        if (insErr) throw insErr;
      }

      setImportPreview(null);
      await fetchShifts();

      const warnNote = warnings.length ? ` (${warnings.length} uyarı)` : '';
      setImportMessage(`${shifts.length} vardiya yüklendi.${warnNote}`);
      if (weeks.includes(weekStart) && shopIds.includes(selectedShopId)) {
        // already viewing imported week/shop
      } else if (weeks[0]) {
        setWeekStart(weeks[0]);
      }
    } catch (e) {
      setError(e?.message || 'Excel içe aktarılamadı.');
    } finally {
      setImporting(false);
    }
  };

  const currentWeekIso = getMondayIso();
  const isCurrentWeek = weekStart === currentWeekIso;
  const pageLoading = loading || shopsLoading;

  return (
    <div className="admin-page-content shift-scheduler-page">
      <div className="shift-week-stats">
        <span><strong>{weekStats.shopsReady}/{weekStats.shopCount}</strong> şube tamam</span>
        {weekStats.incompleteDays > 0 ? (
          <span className="shift-week-stats-warn"><strong>{weekStats.incompleteDays}</strong> eksik gün</span>
        ) : (
          <span>
            <strong>{weekStats.vmCount}</strong> VM
            {weekStats.mdCount > 0 ? (
              <> · <strong>{weekStats.mdCount}</strong> MD</>
            ) : null}
          </span>
        )}
      </div>

      <section className="shift-toolbar">
        <div className="shift-toolbar-row">
          <label className="shift-toolbar-field">
            <span>Şube</span>
            <select
              value={selectedShopId ?? ''}
              onChange={(e) => setSelectedShopId(Number(e.target.value) || null)}
              disabled={!shopRows.length}
            >
              {shopRows.map((shop) => {
                const st = shopStatuses.find((s) => s.id === shop.id);
                const suffix = st?.complete ? ' ✓' : st?.incompleteDays ? ` · ${st.incompleteDays}g` : '';
                return (
                  <option key={shop.id} value={shop.id}>
                    {(shopNameById[String(shop.id)] || `Mağaza ${shop.id}`) + suffix}
                  </option>
                );
              })}
            </select>
          </label>

          <div className="shift-week-bar shift-week-bar--inline">
            <button type="button" className="shift-week-nav" onClick={goPrevWeek} aria-label="Önceki hafta">‹</button>
            <div className="shift-week-center">
              <span className="shift-week-label">{formatWeekLabel(weekStart)}</span>
              {!isCurrentWeek ? (
                <button type="button" className="shift-week-today-link" onClick={goCurrentWeek}>Bu hafta</button>
              ) : (
                <span className="shift-week-current-badge">Bu hafta</span>
              )}
            </div>
            <button type="button" className="shift-week-nav" onClick={goNextWeek} aria-label="Sonraki hafta">›</button>
          </div>
        </div>

        <div className="shift-toolbar-actions">
          <button
            type="button"
            className="shift-btn shift-btn--ghost"
            onClick={() => downloadShiftTemplate({
              shops: shopRows,
              weekStart,
              shopId: selectedShopId,
              staff: ekipStaff,
              existingShifts: allShifts.filter((s) => s.week_start === weekStart),
            })}
            disabled={!shopRows.length}
          >
            Şablon indir
          </button>
          <button
            type="button"
            className="shift-btn shift-btn--primary"
            disabled={!ownerUserId || importing}
            onClick={() => importInputRef.current?.click()}
          >
            {importing ? 'Yükleniyor…' : 'Excel yükle'}
          </button>
          <input
            ref={importInputRef}
            type="file"
            className="shift-import-input"
            accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
            onChange={handleImportFileChange}
          />
        </div>
      </section>

      <section className="shift-import-help">
        <p>
          <strong>MMJ şablonu:</strong> Bir mağazanın planı yeterli — yükleme sonrası aynı saatler ve isimler <strong>tüm şubelere</strong> kopyalanır.
          Her şubede personel Ekip&apos;ten eşleştirilir (o şubeye atanmış olanlar).
          GÖREVİ: <strong>VM</strong> = vardiya müdürü, <strong>MD</strong> = müdür. Hücre: <code>08.00-16.30</code> veya <code>İZİNLİ</code>.
        </p>
        <label className="shift-import-mode">
          <input
            type="checkbox"
            checked={applyToAllShops}
            onChange={(e) => setApplyToAllShops(e.target.checked)}
            disabled={importing}
          />
          Tüm şubelere uygula (önerilen)
        </label>
        <label className="shift-import-mode">
          <input
            type="radio"
            name="shift-import-mode"
            checked={importMode === 'replace'}
            onChange={() => setImportMode('replace')}
            disabled={importing}
          />
          Mevcut haftayı değiştir (önerilen)
        </label>
      </section>

      {importMessage ? <p className="shift-import-success">{importMessage}</p> : null}
      {error ? <div className="shift-alert" role="alert">{error}</div> : null}

      {importPreview ? (
        <section className="shift-import-preview">
          <div className="shift-import-preview-head">
            <div>
              <h2>Önizleme — {importPreview.fileName}</h2>
              <p>
                {importPreview.format === 'mmj-matrix' ? 'MMJ tablo' : 'Liste'}
                {importPreview.appliedToAllShops ? ' · tüm şubeler' : ''} ·{' '}
                {importPreview.stats.shifts} vardiya · {importPreview.stats.shops} şube · {importPreview.stats.weeks} hafta
              </p>
            </div>
            <button
              type="button"
              className="shift-btn shift-btn--ghost"
              onClick={() => setImportPreview(null)}
              disabled={importing}
            >
              İptal
            </button>
          </div>

          {importPreview.errors?.length > 0 ? (
            <div className="shift-alert" role="alert">
              <strong>{importPreview.errors.length} hata</strong>
              <ul className="shift-preview-list">
                {importPreview.errors.slice(0, 8).map((e) => (
                  <li key={`err-${e.row}-${e.message}`}>Satır {e.row}: {e.message}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {importPreview.warnings?.length > 0 ? (
            <div className="shift-alert shift-alert--info" role="status">
              <strong>{importPreview.warnings.length} uyarı</strong>
              <ul className="shift-preview-list">
                {importPreview.warnings.slice(0, 6).map((w, i) => (
                  <li key={`warn-${i}`}>{w.message}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="shift-table-wrap">
            <table className="shift-plan-table">
              <thead>
                <tr>
                  <th>Şube</th>
                  <th>Hafta</th>
                  <th>Gün</th>
                  <th>Vardiya</th>
                  <th>Saat</th>
                  <th>Personel</th>
                </tr>
              </thead>
              <tbody>
                {importPreview.shifts.slice(0, 40).map((s) => (
                  <tr key={`${s.shopId}-${s.weekStart}-${s.day}-${s.startTime}-${s.endTime}`}>
                    <td>{s.shopName}</td>
                    <td>{s.weekStart}</td>
                    <td>{s.day}</td>
                    <td>{s.slotLabel}</td>
                    <td>{s.startTime} – {s.endTime}</td>
                    <td>{formatPeoplePreviewText(s.people, s.assigned_user_ids, staffByAuthId)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {importPreview.shifts.length > 40 ? (
            <p className="shift-preview-more">+ {importPreview.shifts.length - 40} vardiya daha</p>
          ) : null}

          <button
            type="button"
            className="shift-btn shift-btn--primary"
            onClick={runImport}
            disabled={importing || !ownerUserId || importPreview.errors?.length > 0}
          >
            {importing ? 'Yükleniyor…' : `${importPreview.shifts.length} vardiyayı yükle`}
          </button>
        </section>
      ) : null}

      <section className="shift-week-view">
        <h2 className="shift-week-view-title">
          {selectedShopName || 'Şube seçin'} — {formatWeekLabel(weekStart)}
        </h2>
        <p className="shift-week-legend">
          <span><ShiftRoleBadge role="VM" /> Vardiya müdürü</span>
          <span><ShiftRoleBadge role="MD" /> Müdür</span>
        </p>

        {pageLoading ? (
          <p className="shift-loading-inline">Yükleniyor…</p>
        ) : !selectedShopId ? (
          <p className="shift-loading-inline">Önce bir şube ekleyin.</p>
        ) : (
          <div className="shift-table-wrap">
            <table className="shift-plan-table shift-plan-table--week">
              <thead>
                <tr>
                  <th>Gün</th>
                  <th>Vardiyalar</th>
                </tr>
              </thead>
              <tbody>
                {weekRows.map((row) => (
                  <tr key={row.value}>
                    <td className="shift-day-label">
                      <strong>{row.label}</strong>
                      <span>{row.short}</span>
                    </td>
                    <td className="shift-day-shifts">
                      <DayManagersSummary shifts={row.shifts} staffByAuthId={staffByAuthId} />
                      {row.shifts.length === 0 ? (
                        <span className="shift-cell-empty">—</span>
                      ) : (
                        row.shifts.map((shift) => (
                          <WeekShiftCell
                            key={shift.id}
                            shift={shift}
                            staffByAuthId={staffByAuthId}
                          />
                        ))
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

export default ShiftSchedulerPage;
